import React from 'react';
import '../styles/UptimeStats.css';

const DAY_SECONDS = 24 * 60 * 60;
const HEARTBEAT_INTERVAL_SECONDS = 30;
const CONFIRMED_OUTAGE_SECONDS = 180;

const formatPercentage = (value) => {
  const rounded = Math.max(0, Math.min(100, value)).toFixed(2);
  return `${Number(rounded)}%`;
};

const formatDowntime = (seconds) => {
  const totalMinutes = Math.max(0, Math.floor((Number(seconds) || 0) / 60));
  if (totalMinutes === 0) return null;

  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;
  const parts = [];

  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  return parts.join(' ');
};

// Completed downtime comes from Firestore. During a current confirmed outage,
// live downtime is added here only while StatusPage has a fresh server-only
// Firebase read. StatusPage hides this component if that confirmation expires,
// so an old phone cache can never manufacture downtime.
export default function UptimeStats({ status, now }) {
  const trackingStart = status.uptimeTrackingStartedAt
    ? new Date(status.uptimeTrackingStartedAt).getTime()
    : null;
  if (!trackingStart) return null;

  const calculate = (windowSeconds, recordedDowntime) => {
    const windowStart = windowSeconds
      ? Math.max(trackingStart, now - windowSeconds * 1000)
      : trackingStart;
    const periodSeconds = Math.max(0, (now - windowStart) / 1000);
    let downtime = Number(recordedDowntime) || 0;

    const lastHeartbeat = status.lastHeartbeat
      ? new Date(status.lastHeartbeat).getTime()
      : null;
    const heartbeatAgeSeconds = lastHeartbeat
      ? (now - lastHeartbeat) / 1000
      : 0;

    // This is the same confirmation threshold used by the Windows monitor for
    // notifications and permanent outage records. Once confirmed, count the
    // full outage from the first missed 30-second heartbeat.
    if (lastHeartbeat && heartbeatAgeSeconds >= CONFIRMED_OUTAGE_SECONDS) {
      const outageStart = lastHeartbeat + HEARTBEAT_INTERVAL_SECONDS * 1000;
      downtime += Math.max(0, (now - Math.max(outageStart, windowStart)) / 1000);
    }

    const uptime = periodSeconds === 0
      ? 100
      : 100 * Math.max(0, periodSeconds - downtime) / periodSeconds;
    return { uptime, downtime };
  };

  const values = [
    ['Last 24 hours', calculate(DAY_SECONDS, status.uptimeDowntime24Hours)],
    ['Last 7 days', calculate(7 * DAY_SECONDS, status.uptimeDowntime7Days)],
    ['Last 30 days', calculate(30 * DAY_SECONDS, status.uptimeDowntime30Days)],
    ['Lifetime', calculate(null, status.uptimeTotalDowntimeSeconds)]
  ];

  return (
    <section className="uptime-stats" aria-label="Uptime statistics">
      {values.map(([label, value]) => {
        const downtimeText = formatDowntime(value.downtime);
        return (
          <div className="uptime-stat" key={label}>
            <h3>{label}</h3>
            <strong>{formatPercentage(value.uptime)}</strong>
            <span>Overall uptime</span>
            <span className="downtime-line">
              {downtimeText ? (
                <><b className="downtime-value">{downtimeText}</b> down</>
              ) : (
                'No downtime'
              )}
            </span>
          </div>
        );
      })}
    </section>
  );
}
